/**
 * Banner que invita al usuario a activar las notificaciones push.
 * Se muestra solo cuando:
 *   - El navegador soporta web push y la VAPID está configurada
 *   - El permiso aún NO fue concedido (default) — si está denied lo decimos
 *   - El usuario no lo cerró manualmente (recordado en localStorage)
 */

import { useEffect, useState } from "react";
import { Bell, X, Sparkles } from "lucide-react";
import Button from "@/components/ui/Button";
import {
  enableWebPush,
  isPushConfigured,
  isPushSupported,
} from "@/lib/services/push";
import { notificationService } from "@/lib/services/notifications";
import { useAuthStore } from "@/stores/authStore";

const DISMISS_KEY = "push_banner_dismissed_v1";
// Volver a mostrar pasados N días si el usuario lo descartó.
const REMIND_AFTER_DAYS = 14;

export default function EnablePushBanner() {
  const uid = useAuthStore((s) => s.user?.uid);
  const [show, setShow] = useState(false);
  const [permission, setPermission] = useState<
    NotificationPermission | "default"
  >("default");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    (async () => {
      const supported = await isPushSupported();
      if (!supported || !isPushConfigured()) return;

      const perm = Notification.permission as NotificationPermission;
      if (cancelled) return;
      setPermission(perm);

      // Ya está activado → no mostrar.
      if (perm === "granted") return;

      // Dismiss reciente → no mostrar.
      try {
        const raw = localStorage.getItem(DISMISS_KEY);
        if (raw) {
          const ts = Number(raw);
          if (
            Number.isFinite(ts) &&
            Date.now() - ts < REMIND_AFTER_DAYS * 24 * 60 * 60 * 1000
          ) {
            return;
          }
        }
      } catch {
        /* ignore */
      }

      setShow(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setShow(false);
  };

  const handleEnable = async () => {
    if (!uid) return;
    setBusy(true);
    setError(null);
    try {
      const res = await enableWebPush(uid);
      if (res.ok) {
        // Marcar también pushEnabled en prefs (merge con lo que hubiera).
        try {
          await notificationService.updatePrefs(uid, {
            emailEnabled: true,
            pushEnabled: true,
          } as any);
        } catch {
          /* prefs opcional, no romper UX */
        }
        setPermission("granted");
        setShow(false);
      } else {
        const map: Record<string, string> = {
          denied:
            "Permiso denegado. Habilítalo desde el ícono de candado en la URL.",
          unsupported: "Tu navegador no soporta notificaciones push.",
          not_configured: "Push aún no está configurado en este entorno.",
          error: "No se pudo activar. Intenta de nuevo.",
        };
        setError(map[res.reason] || "No se pudo activar.");
        if (res.reason === "denied") setPermission("denied");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;

  const isDenied = permission === "denied";

  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 shadow-sm">
      <button
        type="button"
        onClick={dismiss}
        aria-label="Cerrar"
        className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white/60 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center">
        <div className="shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
          <Bell className="w-6 h-6 text-white" />
        </div>

        <div className="flex-1 min-w-0 pr-6">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-emerald-600 shrink-0" />
            <p className="font-semibold text-gray-900">
              Activa las notificaciones del campamento
            </p>
          </div>
          <p className="text-sm text-gray-600">
            Recibe en tiempo real avisos de inscripciones, pagos, eventos y
            actualizaciones del club aunque tengas la app cerrada.
          </p>
          {error && <p className="text-xs text-rose-600 mt-2">{error}</p>}
          {isDenied && !error && (
            <p className="text-xs text-amber-700 mt-2">
              Las notificaciones están bloqueadas en tu navegador. Habilítalas
              desde el ícono de candado junto a la URL y vuelve a intentarlo.
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={dismiss}
            className="border-emerald-200 text-emerald-700 hover:bg-white"
          >
            Más tarde
          </Button>
          <Button
            onClick={handleEnable}
            disabled={busy || isDenied}
            className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700"
          >
            {busy ? "Activando…" : "Activar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
